import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from '../context/AuthContext';
import api from "../utils/api";
import DoctorSidebar from "./DoctorSidebar";
import "./DoctorAppointments.css";

const DoctorAppointments = () => {
  const { doctorUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [acceptedAppointments, setAcceptedAppointments] = useState([]);
  const [rescheduledAppointments, setRescheduledAppointments] = useState([]);
  const [completedAppointments, setCompletedAppointments] = useState([]);
  const [pastAppointments, setPastAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({ date: "", time: "", reason: "" });
  const [rejectReason, setRejectReason] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [filterPatient, setFilterPatient] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [consultationNotes, setConsultationNotes] = useState("");
  const [processingIds, setProcessingIds] = useState(new Set());
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const rescheduleReasons = ["Doctor Unavailable", "Emergency", "Patient Request", "Other"];
  const currentDate = new Date();

  const fetchAppointments = async () => {
    setIsLoading(true);
    setError(null); // Clear previous errors
    if (!doctorUser || doctorUser.role !== "doctor") {
      setError("You must be logged in as a doctor to view appointments.");
      setTimeout(() => navigate('/doctor-login'), 2000);
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.get(`/appointments/doctor/${doctorUser.id}`, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
      });
      const appointments = response.data;

      setPendingAppointments(
        appointments.filter((app) => app.status.toLowerCase() === "pending" && new Date(app.date) >= currentDate)
      );
      setAcceptedAppointments(
        appointments.filter((app) => app.status.toLowerCase() === "accepted" && new Date(app.date) >= currentDate)
      );
      setRescheduledAppointments(
        appointments.filter((app) => {
          const effectiveDate = app.rescheduledDate ? new Date(app.rescheduledDate) : new Date(app.date);
          return app.status.toLowerCase() === "rescheduled" && effectiveDate >= currentDate;
        })
      );
      setCompletedAppointments(
        appointments.filter((app) => app.status.toLowerCase() === "completed")
      );
      setPastAppointments(
        appointments.filter((app) => {
          const effectiveDate = app.rescheduledDate ? new Date(app.rescheduledDate) : new Date(app.date);
          return effectiveDate < currentDate && app.status.toLowerCase() !== "completed";
        })
      );
    } catch (error) {
      setError(`Failed to load appointments: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, [doctorUser, navigate]);

  useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const filterAppointments = (appointments) => {
    return appointments.filter((app) => {
      const effectiveDate = app.rescheduledDate ? new Date(app.rescheduledDate) : new Date(app.date);
      const dateMatch = filterDate ? effectiveDate.toISOString().split("T")[0] === filterDate : true;
      const patientMatch = filterPatient ? app.name.toLowerCase().includes(filterPatient.toLowerCase()) : true;
      const statusMatch = filterStatus ? app.status.toLowerCase() === filterStatus.toLowerCase() : true;
      return dateMatch && patientMatch && statusMatch;
    });
  };

  const handleAccept = async (appointmentId) => {
    if (processingIds.has(appointmentId)) return;
    setProcessingIds((prev) => new Set(prev).add(appointmentId));
    const originalPending = [...pendingAppointments];

    setPendingAppointments((prev) => prev.filter((app) => app._id !== appointmentId));
    setAcceptedAppointments((prev) => [...prev, { ...pendingAppointments.find((app) => app._id === appointmentId), status: "accepted" }]);

    try {
      await api.put(`/appointments/${appointmentId}/accept`, {}, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
      });
      await fetchAppointments();
      setSuccessMessage("Appointment accepted successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      setError(`Failed to accept appointment: ${error.response?.data?.error || error.message}`);
      setPendingAppointments(originalPending); // Rollback on failure
      setAcceptedAppointments((prev) => prev.filter((app) => app._id !== appointmentId));
    } finally {
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(appointmentId);
        return newSet;
      });
    }
  };

  const handleReject = (appointment) => {
    setSelectedAppointment(appointment);
    setShowRejectModal(true);
    setRejectReason("");
  };

  const handleRejectSubmit = async () => {
    if (!selectedAppointment || processingIds.has(selectedAppointment._id)) return;
    setProcessingIds((prev) => new Set(prev).add(selectedAppointment._id));
    const originalPending = [...pendingAppointments];

    setPendingAppointments((prev) => prev.filter((app) => app._id !== selectedAppointment._id));

    try {
      await api.put(`/appointments/${selectedAppointment._id}/reject`, { reason: rejectReason }, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
      });
      await fetchAppointments();
      setSuccessMessage("Appointment rejected successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      setError(`Failed to reject appointment: ${error.response?.data?.error || error.message}`);
      setPendingAppointments(originalPending); // Rollback on failure
    } finally {
      setShowRejectModal(false);
      setSelectedAppointment(null);
      setRejectReason("");
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(selectedAppointment._id);
        return newSet;
      });
    }
  };

  const handleReschedule = (appointment) => {
    setSelectedAppointment(appointment);
    setShowRescheduleModal(true);
    setRescheduleData({ date: "", time: "", reason: "" });
  };

  const handleRescheduleSubmit = async () => {
    if (!selectedAppointment || processingIds.has(selectedAppointment._id)) return;
    setProcessingIds((prev) => new Set(prev).add(selectedAppointment._id));
    const originalPending = [...pendingAppointments];
    const originalPast = [...pastAppointments];

    const updatedApp = {
      ...selectedAppointment,
      status: "rescheduled",
      rescheduledDate: `${rescheduleData.date}T${rescheduleData.time}:00`,
    };
    setPendingAppointments((prev) => prev.filter((app) => app._id !== selectedAppointment._id));
    setPastAppointments((prev) => prev.filter((app) => app._id !== selectedAppointment._id));
    setRescheduledAppointments((prev) => [...prev, updatedApp]);

    try {
      const newDateTime = `${rescheduleData.date}T${rescheduleData.time}:00`;
      await api.put(`/appointments/${selectedAppointment._id}/reschedule`, { 
        date: newDateTime, 
        reason: rescheduleData.reason 
      }, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
      });
      await fetchAppointments();
      setSuccessMessage("Appointment successfully rescheduled!");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      setError(`Failed to reschedule: ${error.response?.data?.error || error.message}`);
      setPendingAppointments(originalPending); // Rollback on failure
      setPastAppointments(originalPast);
      setRescheduledAppointments((prev) => prev.filter((app) => app._id !== selectedAppointment._id));
    } finally {
      setShowRescheduleModal(false);
      setSelectedAppointment(null);
      setRescheduleData({ date: "", time: "", reason: "" });
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(selectedAppointment._id);
        return newSet;
      });
    }
  };

  const handleStartConsultation = (appointmentId) => {
    navigate(`/doctor/video-call/${appointmentId}`); // Changed to use navigate for SPA behavior
  };

  const handleViewPatientRecords = (patient) => {
    const patientId = patient?._id?.toString();
    if (patientId && typeof patientId === 'string' && patientId.length === 24) { // Basic ObjectId length check
      navigate(`/doctor/patient-records/${patientId}`);
    } else {
      setError("Unable to view patient records: Invalid patient ID.");
      setTimeout(() => {
        setError(null);
        navigate('/doctor-login');
      }, 2000);
    }
  };

  const handleAddNotes = (appointment) => {
    setSelectedAppointment(appointment);
    setConsultationNotes(appointment.notes || "");
    setShowNotesModal(true);
  };

  const handleNotesSubmit = async () => {
    if (!selectedAppointment) return;
    try {
      await api.put(`/appointments/${selectedAppointment._id}/notes`, { notes: consultationNotes }, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
      });
      await fetchAppointments();
      setSuccessMessage("Notes saved successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      setError(`Failed to save notes: ${error.response?.data?.error || error.message}`);
    } finally {
      setShowNotesModal(false);
      setSelectedAppointment(null);
      setConsultationNotes("");
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  if (isLoading) {
    return (
      <div className="doctor-appointments-container">
        <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className="appointments-content">
          <button className="sidebar-toggle" onClick={toggleSidebar} aria-label="Toggle sidebar">
            {sidebarOpen ? "✕" : "☰"}
          </button>
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Loading your TureMedicine appointments...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="doctor-appointments-container">
        <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className="appointments-content">
          <button className="sidebar-toggle" onClick={toggleSidebar} aria-label="Toggle sidebar">
            {sidebarOpen ? "✕" : "☰"}
          </button>
          <div className="error-message">
            {error}
            <button onClick={() => { setError(null); navigate("/doctor-login"); }}>Go to Login</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="doctor-appointments-container">
      <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className="appointments-content">
        <button className="sidebar-toggle" onClick={toggleSidebar} aria-label="Toggle sidebar">
          {sidebarOpen ? "✕" : "☰"}
        </button>
        <h1>Doctor Appointments</h1>
        {successMessage && <div className="success-message">{successMessage}</div>}
        {error && <div className="error-message">{error}</div>}

        <div className="filter-section">
          <div className="filter-group">
            <label htmlFor="filterDate">Filter by Date:</label>
            <input
              type="date"
              id="filterDate"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="filterPatient">Filter by Patient:</label>
            <input
              type="text"
              id="filterPatient"
              value={filterPatient}
              onChange={(e) => setFilterPatient(e.target.value)}
              placeholder="Enter patient name"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="filterStatus">Filter by Status:</label>
            <select
              id="filterStatus"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="rescheduled">Rescheduled</option>
              <option value="completed">Completed</option>
              <option value="past">Past</option>
            </select>
          </div>
        </div>

        <div className="appointments-grid">
          <div className="appointment-section">
            <h2>Pending Appointments</h2>
            {filterAppointments(pendingAppointments).length > 0 ? (
              filterAppointments(pendingAppointments).map((app) => (
                <div key={app._id} className="appointment-card">
                  <p><strong>Patient:</strong> {app.name}</p>
                  <p><strong>Age:</strong> {app.age}</p>
                  <p><strong>Gender:</strong> {app.gender}</p>
                  <p><strong>Symptoms:</strong> {app.symptoms}</p>
                  <p><strong>Date:</strong> {new Date(app.date).toLocaleString()}</p>
                  <div className="appointment-actions">
                    <button onClick={() => handleAccept(app._id)} disabled={processingIds.has(app._id)} aria-label="Accept appointment">
                      {processingIds.has(app._id) ? "Processing..." : "Accept"}
                    </button>
                    <button onClick={() => handleReject(app)} disabled={processingIds.has(app._id)} aria-label="Reject appointment">
                      {processingIds.has(app._id) ? "Processing..." : "Reject"}
                    </button>
                    <button onClick={() => handleReschedule(app)} disabled={processingIds.has(app._id)} aria-label="Reschedule appointment">
                      {processingIds.has(app._id) ? "Processing..." : "Reschedule"}
                    </button>
                    <button onClick={() => handleViewPatientRecords(app.patientId)} aria-label="View patient records">
                      View Records
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p>No pending appointments match the filters.</p>
            )}
          </div>

          <div className="appointment-section">
            <h2>Accepted Appointments</h2>
            {filterAppointments(acceptedAppointments).length > 0 ? (
              filterAppointments(acceptedAppointments).map((app) => (
                <div key={app._id} className="appointment-card">
                  <p><strong>Patient:</strong> {app.name}</p>
                  <p><strong>Age:</strong> {app.age}</p>
                  <p><strong>Gender:</strong> {app.gender}</p>
                  <p><strong>Symptoms:</strong> {app.symptoms}</p>
                  <p><strong>Date:</strong> {new Date(app.date).toLocaleString()}</p>
                  <div className="appointment-actions">
                    <button onClick={() => handleStartConsultation(app._id)} aria-label="Start consultation">
                      Start Consultation
                    </button>
                    <button onClick={() => handleViewPatientRecords(app.patientId)} aria-label="View patient records">
                      View Records
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p>No accepted appointments match the filters.</p>
            )}
          </div>

          <div className="appointment-section">
            <h2>Rescheduled Appointments</h2>
            {filterAppointments(rescheduledAppointments).length > 0 ? (
              filterAppointments(rescheduledAppointments).map((app) => (
                <div key={app._id} className="appointment-card">
                  <p><strong>Patient:</strong> {app.name}</p>
                  <p><strong>Age:</strong> {app.age}</p>
                  <p><strong>Gender:</strong> {app.gender}</p>
                  <p><strong>Symptoms:</strong> {app.symptoms}</p>
                  <p><strong>Original Date:</strong> {new Date(app.date).toLocaleString()}</p>
                  {app.rescheduledDate && (
                    <p><strong>Rescheduled Date:</strong> {new Date(app.rescheduledDate).toLocaleString()}</p>
                  )}
                  <div className="appointment-actions">
                    <button onClick={() => handleStartConsultation(app._id)} aria-label="Start consultation">
                      Start Consultation
                    </button>
                    <button onClick={() => handleViewPatientRecords(app.patientId)} aria-label="View patient records">
                      View Records
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p>No rescheduled appointments match the filters.</p>
            )}
          </div>

          <div className="appointment-section">
            <h2>Past Appointments</h2>
            {filterAppointments(pastAppointments).length > 0 ? (
              filterAppointments(pastAppointments).map((app) => (
                <div key={app._id} className="appointment-card past">
                  <p><strong>Patient:</strong> {app.name}</p>
                  <p><strong>Age:</strong> {app.age}</p>
                  <p><strong>Gender:</strong> {app.gender}</p>
                  <p><strong>Symptoms:</strong> {app.symptoms}</p>
                  <p><strong>Original Date:</strong> {new Date(app.date).toLocaleString()}</p>
                  {app.rescheduledDate && (
                    <p><strong>Rescheduled Date:</strong> {new Date(app.rescheduledDate).toLocaleString()}</p>
                  )}
                  <p><strong>Status:</strong> {app.status}</p>
                  {app.notes && <p><strong>Notes:</strong> {app.notes}</p>}
                  <div className="appointment-actions">
                    <button onClick={() => handleReschedule(app)} disabled={processingIds.has(app._id)} aria-label="Reschedule appointment">
                      {processingIds.has(app._id) ? "Processing..." : "Reschedule"}
                    </button>
                    <button onClick={() => handleViewPatientRecords(app.patientId)} aria-label="View patient records">
                      View Records
                    </button>
                    <button onClick={() => handleAddNotes(app)} aria-label="Add notes">
                      Add Notes
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p>No past appointments match the filters.</p>
            )}
          </div>

          <div className="appointment-section">
            <h2>Completed Appointments</h2>
            {filterAppointments(completedAppointments).length > 0 ? (
              filterAppointments(completedAppointments).map((app) => (
                <div key={app._id} className="appointment-card completed">
                  <p><strong>Patient:</strong> {app.name}</p>
                  <p><strong>Age:</strong> {app.age}</p>
                  <p><strong>Gender:</strong> {app.gender}</p>
                  <p><strong>Symptoms:</strong> {app.symptoms}</p>
                  <p><strong>Date:</strong> {new Date(app.date).toLocaleString()}</p>
                  {app.notes && <p><strong>Notes:</strong> {app.notes}</p>}
                  <div className="appointment-actions">
                    <button onClick={() => handleViewPatientRecords(app.patientId)} aria-label="View patient records">
                      View Records
                    </button>
                    <button onClick={() => handleAddNotes(app)} aria-label="Edit notes">
                      Edit Notes
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p>No completed appointments match the filters.</p>
            )}
          </div>
        </div>

        {showRescheduleModal && (
          <div className="modal">
            <div className="modal-content">
              <h3>Reschedule Appointment</h3>
              <div className="form-group">
                <label htmlFor="rescheduleDate">New Date:</label>
                <input
                  type="date"
                  id="rescheduleDate"
                  value={rescheduleData.date}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, date: e.target.value })}
                  min={new Date().toISOString().split("T")[0]}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="rescheduleTime">New Time:</label>
                <input
                  type="time"
                  id="rescheduleTime"
                  value={rescheduleData.time}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, time: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="rescheduleReason">Reason:</label>
                <select
                  id="rescheduleReason"
                  value={rescheduleData.reason}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, reason: e.target.value })}
                  required
                >
                  <option value="">Select a reason</option>
                  {rescheduleReasons.map((reason) => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button 
                  onClick={handleRescheduleSubmit} 
                  disabled={processingIds.has(selectedAppointment?._id)} 
                  aria-label="Confirm reschedule"
                >
                  {processingIds.has(selectedAppointment?._id) ? "Processing..." : "Confirm"}
                </button>
                <button 
                  onClick={() => setShowRescheduleModal(false)} 
                  aria-label="Cancel reschedule"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {showRejectModal && (
          <div className="modal">
            <div className="modal-content">
              <h3>Reject Appointment</h3>
              <div className="form-group">
                <label htmlFor="rejectReason">Reason for Rejection:</label>
                <textarea
                  id="rejectReason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows="4"
                  placeholder="Please provide a reason for rejecting this appointment"
                  required
                />
              </div>
              <div className="modal-actions">
                <button 
                  onClick={handleRejectSubmit} 
                  disabled={processingIds.has(selectedAppointment?._id)} 
                  aria-label="Confirm reject"
                >
                  {processingIds.has(selectedAppointment?._id) ? "Processing..." : "Confirm"}
                </button>
                <button 
                  onClick={() => setShowRejectModal(false)} 
                  aria-label="Cancel reject"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {showNotesModal && (
          <div className="modal">
            <div className="modal-content">
              <h3>Add/Edit Consultation Notes</h3>
              <div className="form-group">
                <label htmlFor="consultationNotes">Notes:</label>
                <textarea
                  id="consultationNotes"
                  value={consultationNotes}
                  onChange={(e) => setConsultationNotes(e.target.value)}
                  rows="5"
                  placeholder="Enter consultation notes..."
                />
              </div>
              <div className="modal-actions">
                <button onClick={handleNotesSubmit} aria-label="Save notes">Save</button>
                <button onClick={() => setShowNotesModal(false)} aria-label="Cancel notes">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorAppointments;